/**
 * ManualAgreementModal – opprett/rediger en manuell samarbeid-avtale
 * (utenfor-siden). Sperrer dagen automatisk når lagret.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { X, Trash2 } from "lucide-react";
import api from "../../lib/api";

const TYPES = [
  { k: "sfs", label: "SFS" },
  { k: "mass", label: "MASS" },
  { k: "l4l", label: "L4L" },
  { k: "story_swap", label: "Story swap" },
  { k: "other", label: "Annet" },
];

const PLATFORMS = [
  { k: "", label: "Velg plattform…" },
  { k: "snapchat", label: "Snapchat" },
  { k: "telegram", label: "Telegram" },
  { k: "onlyfans", label: "OnlyFans" },
  { k: "fansly", label: "Fansly" },
  { k: "instagram", label: "Instagram" },
  { k: "x", label: "X / Twitter" },
  { k: "tiktok", label: "TikTok" },
  { k: "reddit", label: "Reddit" },
  { k: "bluesky", label: "Bluesky" },
  { k: "other", label: "Annet" },
];

export default function ManualAgreementModal({ initialDate, initialType, initialPlatform, existing, onClose, onSaved, onDeleted }) {
  const [date, setDate] = useState(existing?.date || initialDate || "");
  const [type, setType] = useState(existing?.type || initialType || "sfs");
  const [typeOther, setTypeOther] = useState(existing?.type_other_label || "");
  const [handle, setHandle] = useState(existing?.partner_handle || "");
  const [platform, setPlatform] = useState(existing?.partner_platform || initialPlatform || "");
  const [note, setNote] = useState(existing?.note || "");
  const [busy, setBusy] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [repeatMonths, setRepeatMonths] = useState(3);

  // Beregn datoliste for gjentakelse (samme logikk som backend)
  const repeatDates = React.useMemo(() => {
    if (!repeat || !date) return [];
    const [y, m, d] = date.split("-").map(Number);
    const out = [];
    for (let i = 1; i <= repeatMonths; i++) {
      const newM0 = m - 1 + i; // 0-basert
      const newY = y + Math.floor(newM0 / 12);
      const newM = (newM0 % 12) + 1;
      // Klipp dagen til siste gyldige dag i måneden
      const lastDay = new Date(newY, newM, 0).getDate();
      const day = Math.min(d, lastDay);
      out.push(`${newY}-${String(newM).padStart(2, "0")}-${String(day).padStart(2, "0")}`);
    }
    return out;
  }, [repeat, date, repeatMonths]);

  const save = async () => {
    if (!date) { toast.error("Velg dato."); return; }
    if (type === "other" && !typeOther.trim()) { toast.error("Skriv inn hva slags avtale (Annet)."); return; }
    setBusy(true);
    try {
      const payload = {
        date, type,
        type_other_label: type === "other" ? typeOther.trim() : null,
        partner_handle: handle.trim() || null,
        partner_platform: platform || null,
        note: note.trim() || null,
        repeat_months: (repeat && !existing?.id) ? repeatMonths : 0,
      };
      let saved;
      if (existing?.id) {
        const r = await api.patch(`/collab/manual-agreements/${existing.id}`, payload);
        saved = r.data;
        toast.success("Avtale oppdatert.");
      } else {
        const r = await api.post("/collab/manual-agreements", payload);
        saved = r.data;
        if (r.data?.total && r.data.total > 1) {
          const c = r.data.created?.length || 0;
          const s = r.data.skipped?.length || 0;
          toast.success(`${c} avtaler lagret${s > 0 ? ` (${s} hoppet over — fantes allerede)` : ""}. Dagene er markert som opptatt.`);
        } else {
          toast.success("Avtale lagret. Dagen er markert som opptatt.");
        }
      }
      onSaved?.(saved);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre.");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!existing?.id) return;
    if (!window.confirm("Slette denne avtalen? Dagen blir frigjort.")) return;
    setBusy(true);
    try {
      await api.delete(`/collab/manual-agreements/${existing.id}`);
      toast.success("Avtale slettet.");
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke slette.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/80 backdrop-blur-sm p-4 pt-24 pb-12 overflow-y-auto"
      onClick={onClose} data-testid="manual-agreement-modal">
      <div className="max-w-md w-full panel-tight p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-3 top-3 text-[var(--cream-faint)] hover:text-[var(--magenta)]">
          <X className="h-4 w-4" />
        </button>
        <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-1">
          {existing?.id ? "REDIGER MANUELL AVTALE" : "NY MANUELL AVTALE"}
        </div>
        <h3 className="font-fraunces italic text-2xl text-[var(--cream)] mb-5">Utenfor-siden avtale</h3>

        <div className="space-y-4">
          <div>
            <label className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] block mb-1">DATO</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="input-editorial w-full" data-testid="manual-date" />
          </div>

          <div>
            <label className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] block mb-1">TYPE</label>
            <select value={type} onChange={(e) => setType(e.target.value)}
              className="input-editorial w-full" data-testid="manual-type">
              {TYPES.map((tp) => <option key={tp.k} value={tp.k}>{tp.label}</option>)}
            </select>
            {type === "other" && (
              <input value={typeOther} onChange={(e) => setTypeOther(e.target.value)} maxLength={60}
                placeholder="Hva slags samarbeid?"
                className="input-editorial w-full mt-2" data-testid="manual-type-other" />
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] block mb-1">PLATTFORM</label>
              <select value={platform} onChange={(e) => setPlatform(e.target.value)}
                className="input-editorial w-full" data-testid="manual-platform">
                {PLATFORMS.map((p) => <option key={p.k} value={p.k}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] block mb-1">HANDLE</label>
              <input value={handle} onChange={(e) => setHandle(e.target.value)} maxLength={80}
                placeholder="@brukernavn"
                className="input-editorial w-full" data-testid="manual-handle" />
            </div>
          </div>

          <div>
            <label className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] block mb-1">NOTAT (valgfritt)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={500}
              placeholder="F.eks. «Betalt 500kr Vipps, gjennomføres 21.09»"
              className="input-editorial w-full font-manrope text-sm" data-testid="manual-note" />
          </div>

          {/* Gjenta avtalen — kun ved oppretting, ikke rediger */}
          {!existing?.id && (
            <div className="pt-3 border-t border-white/10">
              <label className="flex items-center gap-2 cursor-pointer text-xs text-[var(--cream)]">
                <input type="checkbox" checked={repeat}
                  onChange={(e) => setRepeat(e.target.checked)}
                  className="w-4 h-4 accent-[var(--magenta)] cursor-pointer"
                  data-testid="manual-repeat-toggle" />
                <span>Gjenta samme avtale på samme dato i flere måneder framover</span>
              </label>
              {repeat && (
                <div className="mt-3 space-y-2 pl-6">
                  <div className="flex items-center gap-2 text-xs text-[var(--cream-soft)]">
                    <span>Antall måneder framover:</span>
                    <select value={repeatMonths}
                      onChange={(e) => setRepeatMonths(parseInt(e.target.value, 10))}
                      className="input-editorial px-2 py-1 text-sm"
                      data-testid="manual-repeat-months">
                      {[2,3,4,5,6,7,8,9,10,11,12].map((n) => (
                        <option key={n} value={n}>{n} mnd</option>
                      ))}
                    </select>
                  </div>
                  <div className="text-[10px] font-meta text-[var(--cream-faint)]">
                    Vil opprette {repeatDates.length + 1} avtaler totalt:
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto"
                    data-testid="manual-repeat-preview">
                    <span className="text-[10px] font-meta px-1.5 py-0.5 bg-[var(--magenta)]/20 text-[var(--magenta)]">
                      {date} (opprinnelig)
                    </span>
                    {repeatDates.map((d) => (
                      <span key={d}
                        className="text-[10px] font-meta px-1.5 py-0.5 bg-white/5 text-[var(--cream-soft)]">
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 mt-5">
          <button onClick={save} disabled={busy}
            className="btn-magenta flex-1 disabled:opacity-40" data-testid="manual-save">
            {busy ? "Lagrer…" : (existing?.id ? "Oppdater avtale" : "Lagre avtale")}
          </button>
          {existing?.id && (
            <button onClick={del} disabled={busy} type="button"
              className="btn-ghost inline-flex items-center gap-1.5 disabled:opacity-40"
              data-testid="manual-delete">
              <Trash2 className="h-4 w-4" /> Slett
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
